import React, { useState } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Share2, Download, Calendar, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

interface DayResult {
  day: string;
  milhar: string[];
  centena: string[];
  dezena: string[];
  grupo: { num: number; animal: string }[];
  duque: string[][];
  terno: string[][];
}

const BioNumericoStellar = () => {
  const [birthDate, setBirthDate] = useState("");
  const [results, setResults] = useState<DayResult[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const animals = [
    "Avestruz", "Águia", "Burro", "Borboleta", "Cachorro", "Cabra", "Carneiro",
    "Camelo", "Cobra", "Coelho", "Cavalo", "Elefante", "Galo", "Gato", "Jacaré",
    "Leão", "Macaco", "Porco", "Pavão", "Peru", "Touro", "Tigre", "Urso", "Veado", "Vaca"
  ];

  const getAnimalByGroup = (group: number) => {
    return animals[group - 1] || "Animal";
  };

  const calculateNumbers = (birthDate: string): DayResult[] => {
    const date = new Date(birthDate);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const baseSum = day + month + (year % 100);
    const seed = parseInt(`${day}${month}${year}`.replace(/\D/g, '')) % 999999;
    
    const results: DayResult[] = [];
    
    for (let dayOffset = 0; dayOffset < 4; dayOffset++) {
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() + dayOffset);
      const dayName = currentDate.toLocaleDateString('pt-BR', { 
        weekday: 'long', 
        day: '2-digit', 
        month: '2-digit' 
      });

      const dayModifier = currentDate.getDate() + currentDate.getMonth() + 1;
      const adjustedSum = (baseSum + dayOffset * 7 + dayModifier) % 100;
      
      // 3 Milhares
      const milhar1 = String(((seed + dayOffset * 1234 + adjustedSum * 11) % 10000)).padStart(4, '0');
      const milhar2 = String(((seed * 2 + dayOffset * 2345 + adjustedSum * 13) % 10000)).padStart(4, '0');
      const milhar3 = String(((seed * 3 + dayOffset * 3456 + adjustedSum * 17) % 10000)).padStart(4, '0');
      
      // 3 Centenas
      const centena1 = String(((adjustedSum * 7 + dayOffset * 23) % 1000)).padStart(3, '0');
      const centena2 = String(((adjustedSum * 11 + dayOffset * 31) % 1000)).padStart(3, '0');
      const centena3 = String(((adjustedSum * 13 + dayOffset * 37) % 1000)).padStart(3, '0');
      
      // 3 Dezenas
      const dezena1 = String(((adjustedSum * 3 + dayOffset * 7) % 100)).padStart(2, '0');
      const dezena2 = String(((adjustedSum * 5 + dayOffset * 11) % 100)).padStart(2, '0');
      const dezena3 = String(((adjustedSum * 13 + dayOffset * 17) % 100)).padStart(2, '0');
      
      // 3 Grupos (1-25)
      const grupo1 = ((adjustedSum + dayOffset * 3) % 25) + 1;
      const grupo2 = ((adjustedSum * 2 + dayOffset * 5) % 25) + 1;
      const grupo3 = ((adjustedSum * 3 + dayOffset * 7) % 25) + 1;
      
      // 2 Duques
      let duque1Dez1 = dezena1;
      let duque1Dez2 = dezena2;
      while(duque1Dez1 === duque1Dez2) {
        duque1Dez2 = String(((parseInt(duque1Dez2) + 1) % 100)).padStart(2, '0');
      }
      
      let duque2Dez1 = String(((adjustedSum * 7 + dayOffset * 19) % 100)).padStart(2, '0');
      let duque2Dez2 = String(((adjustedSum * 9 + dayOffset * 23) % 100)).padStart(2, '0');
      while(duque2Dez1 === duque2Dez2) {
        duque2Dez2 = String(((parseInt(duque2Dez2) + 2) % 100)).padStart(2, '0');
      }
      
      // 2 Ternos
      let terno1Dez1 = dezena1;
      let terno1Dez2 = dezena2;
      let terno1Dez3 = dezena3;
      while(terno1Dez1 === terno1Dez2) {
        terno1Dez2 = String(((parseInt(terno1Dez2) + 1) % 100)).padStart(2, '0');
      }
      while(terno1Dez3 === terno1Dez1 || terno1Dez3 === terno1Dez2) {
        terno1Dez3 = String(((parseInt(terno1Dez3) + 2) % 100)).padStart(2, '0');
      }
      
      let terno2Dez1 = String(((adjustedSum * 11 + dayOffset * 29) % 100)).padStart(2, '0');
      let terno2Dez2 = String(((adjustedSum * 13 + dayOffset * 31) % 100)).padStart(2, '0');
      let terno2Dez3 = String(((adjustedSum * 17 + dayOffset * 37) % 100)).padStart(2, '0');
      while(terno2Dez1 === terno2Dez2) {
        terno2Dez2 = String(((parseInt(terno2Dez2) + 3) % 100)).padStart(2, '0');
      }
      while(terno2Dez3 === terno2Dez1 || terno2Dez3 === terno2Dez2) {
        terno2Dez3 = String(((parseInt(terno2Dez3) + 4) % 100)).padStart(2, '0');
      }

      const dayResult: DayResult = {
        day: `${dayName.charAt(0).toUpperCase() + dayName.slice(1)}`,
        milhar: [milhar1, milhar2, milhar3],
        centena: [centena1, centena2, centena3],
        dezena: [dezena1, dezena2, dezena3],
        grupo: [
          { num: grupo1, animal: getAnimalByGroup(grupo1) },
          { num: grupo2, animal: getAnimalByGroup(grupo2) },
          { num: grupo3, animal: getAnimalByGroup(grupo3) }
        ],
        duque: [
          [duque1Dez1, duque1Dez2],
          [duque2Dez1, duque2Dez2]
        ],
        terno: [
          [terno1Dez1, terno1Dez2, terno1Dez3],
          [terno2Dez1, terno2Dez2, terno2Dez3]
        ]
      };
      
      results.push(dayResult);
    }
    
    return results;
  };

  const generateNumbers = async () => {
    if (!birthDate) {
      toast.error("Por favor, selecione sua data de nascimento!");
      return;
    }

    setIsGenerating(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const calculatedResults = calculateNumbers(birthDate);
      setResults(calculatedResults);
      toast.success("🌟 Sequência Cósmica gerada com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar sequência cósmica");
    } finally {
      setIsGenerating(false);
    }
  };

  const shareResults = async () => {
    if (navigator.share && results.length > 0) {
      try {
        await navigator.share({
          title: 'BioNumérico Stellar - Meus Números Cósmicos',
          text: `Confira meus números gerados pelo BioNumérico Stellar para os próximos 4 dias!`,
          url: window.location.href
        });
        toast.success("Compartilhado com sucesso!");
      } catch (error) {
        toast.error("Erro ao compartilhar");
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado para área de transferência!");
    }
  };

  const exportToPDF = async () => {
    if (results.length === 0) {
      toast.error("Gere os números primeiro!");
      return;
    }

    try {
      const element = document.getElementById('results-container');
      if (!element) return;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('bionumerico-stellar.pdf');
      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    }
  };

  return (
    <div className="space-y-6">
      <PageInstructions
        title="📖 Como usar o BioNumérico Stellar"
        steps={[
          "Preencha sua data de nascimento completa (dia, mês e ano)",
          "Clique em 'Calcular' para gerar seus números da sorte",
          "O sistema usa numerologia para identificar seus números pessoais",
          "Veja os grupos, dezenas, centenas e milhares sugeridos para você",
          "Anote os resultados e use-os nas suas apostas"
        ]}
      />
      {/* Header */}
      <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold flex items-center justify-center gap-2">
            <Star className="h-8 w-8" />
            BioNumérico Stellar
            <Sparkles className="h-8 w-8" />
          </CardTitle>
          <p className="text-blue-100 text-lg">
            Sistema de Números Cósmicos baseado em Energia Natal
          </p>
        </CardHeader>
      </Card>

      {/* Input Section */}
      <Card className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white border-0">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            Sincronize com sua Frequência Cósmica
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="birthDate" className="text-white font-semibold">
              Data da sua Energia Natal:
            </Label>
            <Input
              id="birthDate"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="bg-white/90 text-gray-900 border-0 focus:bg-white"
            />
          </div>
          <Button 
            onClick={generateNumbers}
            disabled={isGenerating || !birthDate}
            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 text-lg"
          >
            {isGenerating ? (
              <>
                <Sparkles className="h-5 w-5 mr-2 animate-spin" />
                Canalizando Energia Cósmica...
              </>
            ) : (
              <>
                <Star className="h-5 w-5 mr-2" />
                Gerar Sequência Cósmica
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {results.length > 0 && (
        <div id="results-container" className="space-y-6">
          {/* Action Buttons */}
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={shareResults}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Share2 className="h-4 w-4" />
              Compartilhar
            </Button>
            <Button 
              onClick={exportToPDF}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Exportar PDF
            </Button>
          </div>

          {/* How it Works */}
          <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
            <CardHeader>
              <CardTitle className="text-xl">📊 Como Funciona o Cálculo</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-purple-100">
                O sistema utiliza sua data de nascimento como base e aplica algoritmos matemáticos específicos para cada modalidade. 
                Os números são calculados combinando dia, mês, ano com modificadores diários, garantindo uma sequência única e progressiva para cada pessoa.
              </p>
            </CardContent>
          </Card>

          {/* Results for each day */}
          {results.map((result, index) => (
            <Card key={index} className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  {result.day} - Dia {index + 1}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Milhar */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <div className="text-center font-bold text-lg mb-3 border-b border-white/30 pb-2">
                      🎯 MILHAR
                    </div>
                    <div className="text-center text-xl font-bold">
                      {result.milhar.join(' | ')}
                    </div>
                  </div>

                  {/* Centena */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <div className="text-center font-bold text-lg mb-3 border-b border-white/30 pb-2">
                      💯 CENTENA
                    </div>
                    <div className="text-center text-xl font-bold">
                      {result.centena.join(' | ')}
                    </div>
                  </div>

                  {/* Dezena */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <div className="text-center font-bold text-lg mb-3 border-b border-white/30 pb-2">
                      🔢 DEZENA
                    </div>
                    <div className="text-center text-xl font-bold">
                      {result.dezena.join(' | ')}
                    </div>
                  </div>

                  {/* Grupo */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <div className="text-center font-bold text-lg mb-3 border-b border-white/30 pb-2">
                      🦁 GRUPO
                    </div>
                    <div className="text-center text-xl font-bold mb-2">
                      {result.grupo.map(g => g.num.toString().padStart(2, '0')).join(' | ')}
                    </div>
                    <div className="bg-white/20 rounded-lg p-2 text-center text-sm">
                      {result.grupo.map(g => g.animal).join(' | ')}
                    </div>
                  </div>

                  {/* Duque */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <div className="text-center font-bold text-lg mb-3 border-b border-white/30 pb-2">
                      👥 DUQUE
                    </div>
                    <div className="text-center text-xl font-bold">
                      {result.duque.map(d => d.join('-')).join(' | ')}
                    </div>
                  </div>

                  {/* Terno */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <div className="text-center font-bold text-lg mb-3 border-b border-white/30 pb-2">
                      🎲 TERNO
                    </div>
                    <div className="text-center text-xl font-bold">
                      {result.terno.map(t => t.join('-')).join(' | ')}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tips */}
      <Card className="bg-gradient-to-r from-pink-500 to-red-500 text-white border-0">
        <CardHeader>
          <CardTitle className="text-xl">🌟 Características do BioNumérico Stellar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {[
              { icon: "⚡", title: "Algoritmo Quântico", desc: "Transforma sua energia natal em sequências numéricas" },
              { icon: "🌙", title: "Sincronização Temporal", desc: "Os números se alinham com ciclos cósmicos de 4 dias" },
              { icon: "🔮", title: "Matriz Multidimensional", desc: "Cobre todas as modalidades em frequências harmônicas" },
              { icon: "⚖️", title: "Equilíbrio Universal", desc: "Distribui energias entre diferentes campos numerológicos" },
              { icon: "🎯", title: "Código Pessoal", desc: "Cada data gera uma assinatura cósmica única" },
              { icon: "💫", title: "Estratégia Stellar", desc: "Diversifique apostas seguindo a sabedoria estelar" }
            ].map((tip, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-white/10 rounded-lg">
                <span className="text-2xl">{tip.icon}</span>
                <div>
                  <h4 className="font-semibold">{tip.title}</h4>
                  <p className="text-sm text-pink-100">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white/15 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <h4 className="text-center text-xl font-bold mb-4">📊 O Sistema Gerará Para Você:</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[
                "🎯 3 Milhares (0000-9999)",
                "💯 3 Centenas (000-999)",
                "🔢 3 Dezenas (00-99)",
                "🦁 3 Grupos com animais (01-25)",
                "👥 2 Duques (2 dezenas cada)",
                "🎲 2 Ternos (3 dezenas cada)"
              ].map((item, index) => (
                <Badge key={index} variant="secondary" className="bg-white/20 text-white p-2 justify-start">
                  {item}
                </Badge>
              ))}
            </div>
            <div className="text-center mt-4 font-bold text-yellow-200">
              📅 Números para os próximos 4 dias consecutivos
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BioNumericoStellar;